// ═══════════════════════════════════════════════════════════════
// FileOrganizerModal — Modal d'organisation IA des fichiers projet
// S59 — Pattern propose/apply S57-S58 avec cost discipline UX (S58)
// ═══════════════════════════════════════════════════════════════
//
// Écran d'accueil (sans appel LLM) → clic Lancer l'analyse → résultat éditable
// → Appliquer (ou Annuler). Cache session par (projectId, files_signature).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import {
    X, Sparkles, Loader, FolderTree, Folder, FileText, AlertCircle,
    CheckCircle, Trash2, ArrowRight, Info,
} from 'lucide-react';
import {
    organizeProposeFiles, organizeApplyFiles, organizeRollback,
} from '../../services/filesApi';

const TIER_META = {
    kimi:   { label: 'Kimi K2',    est: '≈ $0.02 · ~30s',  color: '#8b5cf6', description: 'Défaut · raisonnement structuré' },
    haiku:  { label: 'Haiku 4.5',  est: '≈ $0.01 · ~15s',  color: '#06b6d4', description: 'Rapide · coût minimal' },
    sonnet: { label: 'Sonnet 4.6', est: '≈ $0.06 · ~25s',  color: '#3b82f6', description: 'Polish · détail fin' },
    opus:   { label: 'Opus 4.7',   est: '≈ $0.30 · ~40s',  color: '#f59e0b', description: 'Premium · cas complexes' },
};

const RISK_META = {
    safe:     { label: 'sûr',     color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
    conflict: { label: 'conflit', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    breaking: { label: 'risqué',  color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

const FileOrganizerModal = ({
    projectId,
    projectName,
    onClose,
    onApplied,
    getCachedResult,
    putCachedResult,
}) => {
    const [modelTier, setModelTier] = useState('kimi');
    const [preserveExisting, setPreserveExisting] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const [excludedMoveIds, setExcludedMoveIds] = useState(new Set());
    const [duplicatesToDelete, setDuplicatesToDelete] = useState(new Set());
    const [lastRollback, setLastRollback] = useState(null);
    const [undoCountdown, setUndoCountdown] = useState(0);
    const initRef = useRef(false);

    // Escape pour fermer (sauf si en cours)
    useEffect(() => {
        const h = (e) => {
            if (e.key === 'Escape' && !analyzing && !applying) onClose?.();
        };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [analyzing, applying, onClose]);

    // Cache hit au mount par (projectId, modelTier, preserveExisting)
    useEffect(() => {
        if (initRef.current) return;
        if (!projectId) return;
        initRef.current = true;
        tryLoadFromCache(modelTier);
    }, [projectId, modelTier]);

    const cacheKey = (tier) => `${tier}:${preserveExisting ? 'preserve' : 'refonte'}`;

    const tryLoadFromCache = (tier) => {
        if (!getCachedResult) return false;
        const cached = getCachedResult(projectId, cacheKey(tier));
        if (cached) {
            setResult(cached);
            setFromCache(true);
            setError('');
            return true;
        }
        return false;
    };

    const runAnalyze = async (tier) => {
        setAnalyzing(true);
        setError('');
        setResult(null);
        setFromCache(false);
        setSuccess(null);
        setExcludedMoveIds(new Set());
        setDuplicatesToDelete(new Set());
        try {
            const res = await organizeProposeFiles(projectId, {
                modelTier: tier,
                preserveExisting,
            });
            setResult(res);
            if (putCachedResult) {
                putCachedResult(projectId, cacheKey(tier), res);
            }
        } catch (err) {
            const detail = err.detail || {};
            if (detail.error === 'too_many_files') {
                setError(`Projet trop volumineux : ${detail.count} fichiers (max ${detail.max}). Utilise le scope par dossier quand l'UI sera complète.`);
            } else if (detail.error === 'llm_output_unparseable') {
                setError('Le modèle a renvoyé une réponse illisible. Essaie un autre tier ou relance.');
            } else if (detail.error === 'forbidden') {
                setError('Droits insuffisants pour organiser ce projet.');
            } else {
                setError(err.message || 'Erreur analyse');
            }
        } finally {
            setAnalyzing(false);
        }
    };

    const handleChangeTier = (tier) => {
        if (tier === modelTier || analyzing || applying) return;
        setModelTier(tier);
        // Check cache pour ce nouveau tier — si hit, affiche direct
        tryLoadFromCache(tier) || setResult(null);
    };

    const handleTogglePreserve = () => {
        if (analyzing || applying) return;
        setPreserveExisting(v => !v);
        // Invalide l'affichage courant (cache a une clé différente)
        setResult(null);
        setFromCache(false);
    };

    const toggleMoveExclusion = (fileId) => {
        setExcludedMoveIds(prev => {
            const next = new Set(prev);
            if (next.has(fileId)) next.delete(fileId);
            else next.add(fileId);
            return next;
        });
    };

    const toggleDuplicateDelete = (fileId) => {
        setDuplicatesToDelete(prev => {
            const next = new Set(prev);
            if (next.has(fileId)) next.delete(fileId);
            else next.add(fileId);
            return next;
        });
    };

    const buildEditedPlan = () => {
        if (!result) return null;
        const editedStructure = result.proposed_structure
            .map(group => ({
                ...group,
                moves: (group.moves || []).filter(m => !excludedMoveIds.has(m.file_id)),
            }))
            .filter(g => g.moves.length > 0);
        return {
            ...result,
            proposed_structure: editedStructure,
            duplicates_to_delete: [...duplicatesToDelete],
        };
    };

    const handleApply = async () => {
        const plan = buildEditedPlan();
        if (!plan) return;
        setApplying(true);
        setError('');
        try {
            const res = await organizeApplyFiles(projectId, plan);
            setSuccess(res);
            setLastRollback(res.log_id ? res : null);
            setUndoCountdown(30);
            // S59 P3 — notifie ProjectSettings (et autres listeners) du changement
            window.dispatchEvent(new CustomEvent('file-organizer-changed', { detail: { projectId, action: 'apply', logId: res.log_id } }));
            if (onApplied) onApplied(res);
        } catch (err) {
            setError(err.message || 'Erreur application');
        } finally {
            setApplying(false);
        }
    };

    const handleUndo = async () => {
        if (!lastRollback?.log_id) return;
        try {
            await organizeRollback(projectId, lastRollback.log_id);
            setSuccess(null);
            setLastRollback(null);
            setUndoCountdown(0);
            // Reset le résultat : le cache devient périmé
            setResult(null);
            setFromCache(false);
            setExcludedMoveIds(new Set());
            setDuplicatesToDelete(new Set());
            // S59 P3 — notifie le refresh de l'historique
            window.dispatchEvent(new CustomEvent('file-organizer-changed', { detail: { projectId, action: 'undo' } }));
            if (onApplied) onApplied({ rolled_back: true });
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur rollback');
        }
    };

    // Compte à rebours undo 30s
    useEffect(() => {
        if (undoCountdown <= 0) return;
        const t = setInterval(() => setUndoCountdown(n => Math.max(0, n - 1)), 1000);
        return () => clearInterval(t);
    }, [undoCountdown]);

    // ═══ Rendu ═══════════════════════════════════════════════════════

    const totalMoves = result
        ? result.proposed_structure.reduce((s, g) => s + (g.moves?.length || 0), 0)
        : 0;
    const effectiveMoves = result
        ? result.proposed_structure.reduce(
            (s, g) => s + (g.moves || []).filter(m => !excludedMoveIds.has(m.file_id)).length, 0)
        : 0;
    const tierMeta = TIER_META[modelTier];

    return (
        <div
            style={overlayStyle}
            onClick={() => { if (!analyzing && !applying) onClose?.(); }}
        >
            <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
                {/* HEADER */}
                <div style={headerStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <div style={headerIconStyle}>
                            <FolderTree size={18} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                                Organiser les fichiers avec l'IA
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {projectName || `Projet #${projectId}`}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={analyzing || applying}
                        style={closeBtnStyle(analyzing || applying)}
                        title="Fermer (Échap)"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* CORPS scrollable */}
                <div style={bodyStyle}>
                    {/* Sélecteur modèle + toggle préservation */}
                    {!success && (
                        <div style={{ marginBottom: 16 }}>
                            <div style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                                Modèle IA
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                                {Object.keys(TIER_META).map(tier => {
                                    const m = TIER_META[tier];
                                    const active = modelTier === tier;
                                    return (
                                        <button
                                            key={tier}
                                            onClick={() => handleChangeTier(tier)}
                                            disabled={analyzing || applying}
                                            style={{
                                                padding: '8px 10px', borderRadius: 8, textAlign: 'left',
                                                background: active ? `${m.color}22` : 'rgba(255,255,255,0.03)',
                                                border: `1px solid ${active ? `${m.color}66` : 'var(--card-border)'}`,
                                                color: active ? m.color : 'var(--text-secondary)',
                                                cursor: (analyzing || applying) ? 'not-allowed' : 'pointer',
                                                fontSize: 11, fontWeight: 500,
                                            }}
                                        >
                                            <div style={{ fontSize: 12, fontWeight: 600 }}>{m.label}</div>
                                            <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>{m.est}</div>
                                            <div style={{ fontSize: 9.5, opacity: 0.7, marginTop: 2 }}>{m.description}</div>
                                        </button>
                                    );
                                })}
                            </div>

                            <label style={toggleLabelStyle}>
                                <input
                                    type="checkbox"
                                    checked={preserveExisting}
                                    onChange={handleTogglePreserve}
                                    disabled={analyzing || applying}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: 12, color: 'var(--text)' }}>Conserver la structure existante</div>
                                    <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 2 }}>
                                        {preserveExisting
                                            ? 'L\'IA ne classe que les fichiers actuellement à la racine.'
                                            : 'Refonte globale : l\'IA peut reclasser tous les fichiers, y compris ceux déjà dans des dossiers.'}
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Écran d'accueil */}
                    {!result && !analyzing && !error && !success && (
                        <div style={entryScreenStyle}>
                            <div style={entryIconStyle}>
                                <Sparkles size={26} />
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
                                Lancer l'analyse
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 18, maxWidth: 520 }}>
                                {tierMeta.label} va lire les fichiers de ton projet et proposer une structure de dossiers
                                thématique. Les résumés déjà générés lors des uploads (Haiku) servent de contexte — aucun
                                nouvel upload nécessaire. Aucun fichier n'est déplacé avant ton clic sur « Appliquer ».
                            </div>
                            <button
                                onClick={() => runAnalyze(modelTier)}
                                style={{
                                    ...primaryBtnStyle,
                                    background: `linear-gradient(180deg, ${tierMeta.color}, ${tierMeta.color}dd)`,
                                    border: `1px solid ${tierMeta.color}`,
                                    padding: '10px 22px', fontSize: 13,
                                }}
                            >
                                <Sparkles size={14} />
                                Analyser ({tierMeta.label})
                            </button>
                            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 10 }}>
                                Coût estimé : <strong>{tierMeta.est}</strong>
                            </div>
                        </div>
                    )}

                    {/* Spinner analyse */}
                    {analyzing && (
                        <div style={spinnerContainerStyle}>
                            <Loader size={16} className="animate-spin" />
                            <span style={{ marginLeft: 8 }}>
                                Analyse en cours… {tierMeta.label}
                            </span>
                        </div>
                    )}

                    {/* Erreur */}
                    {error && !analyzing && (
                        <div style={errorBoxStyle}>
                            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>{error}</div>
                        </div>
                    )}

                    {/* Succès après apply */}
                    {success && (
                        <div style={successBoxStyle}>
                            <CheckCircle size={16} style={{ flexShrink: 0, marginTop: 1, color: '#10b981' }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#10b981', marginBottom: 4 }}>
                                    Organisation appliquée
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                    {success.files_moved} fichier(s) déplacé(s) · {success.folders_created} dossier(s) créé(s)
                                    {success.duplicates_deleted > 0 && ` · ${success.duplicates_deleted} doublon(s) supprimé(s)`}
                                </div>
                                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 3 }}>
                                    Rollback disponible pendant 72h depuis Paramètres → Historique des organisations.
                                </div>
                            </div>
                            {lastRollback && undoCountdown > 0 && (
                                <button onClick={handleUndo} style={undoBtnStyle}>
                                    Annuler ({undoCountdown}s)
                                </button>
                            )}
                        </div>
                    )}

                    {/* Résultat */}
                    {result && !analyzing && !success && (
                        <>
                            {/* Warnings service (filtre dossiers vides, fichiers orphelins en refonte, etc.) */}
                            {Array.isArray(result.warnings) && result.warnings.length > 0 && (
                                <div style={warningsBoxStyle}>
                                    <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1, color: '#f59e0b' }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: '#f59e0b', marginBottom: 4 }}>
                                            {result.warnings.length === 1 ? 'Avertissement' : `${result.warnings.length} avertissements`}
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                                            {result.warnings.map((w, i) => <li key={i} style={{ marginBottom: 2 }}>{w}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {/* Bandeau cache */}
                            {fromCache && (
                                <div style={cacheBadgeStyle}>
                                    <CheckCircle size={11} /> Depuis le cache — pas de nouvelle dépense
                                </div>
                            )}

                            {/* Stats */}
                            <div style={statsRowStyle}>
                                <div><strong>{result.current_state?.total_files || 0}</strong> fichiers</div>
                                <div><strong>{result.proposed_structure?.length || 0}</strong> dossiers proposés</div>
                                <div><strong>{effectiveMoves}</strong> / {totalMoves} déplacements{effectiveMoves !== totalMoves ? ' (édités)' : ''}</div>
                                {result.current_state?.duplicates_detected?.length > 0 && (
                                    <div style={{ color: '#f59e0b' }}>
                                        <strong>{result.current_state.duplicates_detected.length}</strong> doublon(s) détecté(s)
                                    </div>
                                )}
                            </div>

                            {/* Summary IA */}
                            {result.summary && (
                                <div style={summaryBoxStyle}>
                                    <Info size={12} style={{ color: tierMeta.color, flexShrink: 0, marginTop: 2 }} />
                                    <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.55, fontStyle: 'italic' }}>
                                        {result.summary}
                                    </div>
                                </div>
                            )}

                            {/* Doublons */}
                            {result.current_state?.duplicates_detected?.length > 0 && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={sectionTitleStyle}>Doublons détectés</div>
                                    {result.current_state.duplicates_detected.map((dup, idx) => (
                                        <div key={idx} style={duplicateCardStyle}>
                                            <div style={{ fontSize: 11.5, color: 'var(--text)', marginBottom: 6 }}>
                                                Fichiers identiques : <strong>{dup.filenames.join(' · ')}</strong>
                                            </div>
                                            {dup.file_ids.map((fid, i) => (
                                                <label key={fid} style={duplicateRowStyle}>
                                                    <input
                                                        type="checkbox"
                                                        checked={duplicatesToDelete.has(fid)}
                                                        onChange={() => toggleDuplicateDelete(fid)}
                                                    />
                                                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                                                        Supprimer [{fid}] <code style={{ fontSize: 10 }}>{dup.filenames[i]}</code>
                                                        {' '}({new Date(dup.dates[i]).toLocaleString('fr-FR')})
                                                    </span>
                                                </label>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Dossiers proposés */}
                            <div style={sectionTitleStyle}>Structure proposée</div>
                            {result.proposed_structure?.map((group, idx) => (
                                <div key={idx} style={folderGroupStyle}>
                                    <div style={folderHeaderStyle}>
                                        <Folder size={14} style={{ color: '#a78bfa' }} />
                                        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
                                            {group.folder_path}
                                        </span>
                                        {group.is_new && (
                                            <span style={newBadgeStyle}>nouveau</span>
                                        )}
                                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                                            {(group.moves || []).filter(m => !excludedMoveIds.has(m.file_id)).length} fichier(s)
                                        </span>
                                    </div>
                                    {group.rationale && (
                                        <div style={{ fontSize: 10.5, color: 'var(--text-muted)', fontStyle: 'italic', marginBottom: 6 }}>
                                            {group.rationale}
                                        </div>
                                    )}
                                    {(group.moves || []).map((move) => {
                                        const excluded = excludedMoveIds.has(move.file_id);
                                        const risk = RISK_META[move.risk] || RISK_META.safe;
                                        return (
                                            <div
                                                key={move.file_id}
                                                style={{
                                                    ...moveRowStyle,
                                                    opacity: excluded ? 0.4 : 1,
                                                    textDecoration: excluded ? 'line-through' : 'none',
                                                }}
                                            >
                                                <FileText size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 11.5, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {move.filename}
                                                    </div>
                                                    {move.reason && (
                                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>
                                                            {move.reason}
                                                        </div>
                                                    )}
                                                </div>
                                                {move.risk && move.risk !== 'safe' && (
                                                    <span style={{
                                                        padding: '2px 7px', borderRadius: 8,
                                                        background: risk.bg, color: risk.color,
                                                        fontSize: 9.5, fontWeight: 600,
                                                    }}>{risk.label}</span>
                                                )}
                                                <button
                                                    onClick={() => toggleMoveExclusion(move.file_id)}
                                                    style={moveToggleBtnStyle}
                                                    title={excluded ? 'Remettre dans le plan' : 'Retirer du plan'}
                                                >
                                                    {excluded ? '↺' : '×'}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}

                            {/* Meta modèle */}
                            <div style={metaRowStyle}>
                                <span>{result.model_used}</span>
                                {result.duration_ms != null && <span>{(result.duration_ms / 1000).toFixed(1)}s</span>}
                                {result.cost_usd != null && <span>${Number(result.cost_usd).toFixed(4)}</span>}
                            </div>
                        </>
                    )}
                </div>

                {/* FOOTER actions */}
                <div style={footerStyle}>
                    {result && !success ? (
                        <button
                            onClick={() => runAnalyze(modelTier)}
                            disabled={analyzing || applying}
                            style={secondaryBtnStyle(analyzing || applying)}
                            title="Relancer l'analyse avec le tier courant"
                        >
                            <Sparkles size={12} /> Relancer
                        </button>
                    ) : <div />}

                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            onClick={onClose}
                            disabled={applying}
                            style={secondaryBtnStyle(applying)}
                        >
                            Fermer
                        </button>
                        {result && !success && (
                            <button
                                onClick={handleApply}
                                disabled={applying || analyzing || (effectiveMoves === 0 && duplicatesToDelete.size === 0)}
                                style={{
                                    ...primaryBtnStyle,
                                    background: `linear-gradient(180deg, #8b5cf6, #7c3aed)`,
                                    border: `1px solid #6d28d9`,
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    opacity: (applying || analyzing || (effectiveMoves === 0 && duplicatesToDelete.size === 0)) ? 0.5 : 1,
                                }}
                            >
                                {applying ? <Loader size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                                {applying ? 'Application…' : `Appliquer (${effectiveMoves + duplicatesToDelete.size})`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Styles ──────────────────────────────────────────────────────────

const overlayStyle = {
    position: 'fixed', inset: 0, zIndex: 120,
    background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
};
const modalStyle = {
    width: 820, maxWidth: '96vw', maxHeight: '92vh',
    display: 'flex', flexDirection: 'column',
    background: 'var(--card-bg)',
    border: '1px solid var(--card-border)',
    borderRadius: 14,
    boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
    overflow: 'hidden',
};
const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 22px',
    borderBottom: '1px solid var(--card-border)',
    background: 'linear-gradient(180deg, rgba(139,92,246,0.08), transparent)',
};
const headerIconStyle = {
    width: 34, height: 34, borderRadius: 8,
    background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};
const closeBtnStyle = (disabled) => ({
    background: 'transparent', border: 'none',
    color: 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: 4,
});
const bodyStyle = { flex: 1, overflow: 'auto', padding: '18px 22px' };
const footerStyle = {
    padding: '14px 22px',
    borderTop: '1px solid var(--card-border)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 10, flexWrap: 'wrap',
    background: 'var(--card-bg-secondary)',
};
const toggleLabelStyle = {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    padding: '8px 10px', marginTop: 10, borderRadius: 8,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--card-border)',
    cursor: 'pointer',
};
const entryScreenStyle = {
    padding: '36px 24px', borderRadius: 12,
    background: 'rgba(139,92,246,0.04)',
    border: '1px dashed rgba(139,92,246,0.25)',
    textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
};
const entryIconStyle = {
    width: 52, height: 52, borderRadius: 12, marginBottom: 14,
    background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const spinnerContainerStyle = {
    padding: '24px 20px', borderRadius: 10,
    background: 'rgba(139,92,246,0.06)',
    border: '1px solid rgba(139,92,246,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#c4b5fd', fontSize: 13,
};
const errorBoxStyle = {
    padding: '12px 14px', borderRadius: 8, marginBottom: 12,
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.25)',
    color: '#ef4444', fontSize: 12,
    display: 'flex', alignItems: 'flex-start', gap: 8,
};
const successBoxStyle = {
    padding: '12px 14px', borderRadius: 8, marginBottom: 12,
    background: 'rgba(16,185,129,0.08)',
    border: '1px solid rgba(16,185,129,0.3)',
    display: 'flex', alignItems: 'flex-start', gap: 10,
};
const undoBtnStyle = {
    padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500,
    background: 'rgba(245,158,11,0.15)',
    border: '1px solid rgba(245,158,11,0.4)',
    color: '#f59e0b', cursor: 'pointer',
    flexShrink: 0,
};
const warningsBoxStyle = {
    padding: '10px 12px', marginBottom: 12, borderRadius: 8,
    background: 'rgba(245,158,11,0.06)',
    border: '1px solid rgba(245,158,11,0.25)',
    display: 'flex', gap: 8, alignItems: 'flex-start',
};
const cacheBadgeStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 10,
    background: 'rgba(16,185,129,0.12)',
    color: '#10b981', fontSize: 10.5, fontWeight: 600,
    marginBottom: 10,
};
const statsRowStyle = {
    display: 'flex', gap: 16, flexWrap: 'wrap',
    padding: '8px 12px', marginBottom: 10, borderRadius: 8,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--card-border)',
    fontSize: 11.5, color: 'var(--text-secondary)',
};
const summaryBoxStyle = {
    padding: '10px 12px', marginBottom: 14, borderRadius: 8,
    background: 'rgba(139,92,246,0.05)',
    border: '1px solid rgba(139,92,246,0.2)',
    display: 'flex', gap: 8, alignItems: 'flex-start',
};
const sectionTitleStyle = {
    fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8, marginTop: 6,
};
const duplicateCardStyle = {
    padding: '10px 12px', marginBottom: 6, borderRadius: 8,
    background: 'rgba(245,158,11,0.06)',
    border: '1px solid rgba(245,158,11,0.2)',
};
const duplicateRowStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '3px 0', cursor: 'pointer',
};
const folderGroupStyle = {
    padding: '10px 12px', marginBottom: 8, borderRadius: 8,
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid var(--card-border)',
};
const folderHeaderStyle = {
    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
};
const newBadgeStyle = {
    padding: '1px 6px', borderRadius: 10, fontSize: 9,
    background: 'rgba(16,185,129,0.15)',
    color: '#10b981', fontWeight: 600,
    textTransform: 'uppercase',
};
const moveRowStyle = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '5px 0',
};
const moveToggleBtnStyle = {
    width: 20, height: 20, borderRadius: 4,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid var(--card-border)',
    color: 'var(--text-secondary)',
    cursor: 'pointer', fontSize: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};
const metaRowStyle = {
    marginTop: 10, fontSize: 10.5, color: 'var(--text-muted)',
    display: 'flex', gap: 14, flexWrap: 'wrap',
};
const primaryBtnStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    color: '#ffffff', cursor: 'pointer', border: '1px solid transparent',
};
const secondaryBtnStyle = (disabled) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, fontSize: 12,
    background: 'transparent',
    border: '1px solid var(--card-border)',
    color: 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
});

export default FileOrganizerModal;
